import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Btn, Card, Input, Modal, Select, RNLLoader, Textarea } from '../components/UI';
import { Panel, Field, FieldGrid, actionFace, BackBar, ProfileGridStyles, PROFILE_GRID } from '../components/profile-ui';
import Avatar from '../components/Avatar';
import * as api from '../api';
import useUserPhoto from '../hooks/useUserPhoto';
import { canManageUser, errorText, userAdminAccess } from '../utils';

// ─────────────────────────────────────────────
// Formatting
// ─────────────────────────────────────────────

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/** "3 hours ago" / "in 21 hours" — the units people actually think in. */
const relative = (v) => {
  if (!v) return null;
  const then = new Date(v).getTime();
  if (Number.isNaN(then)) return null;
  const secs = Math.round((then - Date.now()) / 1000);
  const abs = Math.abs(secs);
  const units = [
    [60, 'second', 1], [3600, 'minute', 60], [86400, 'hour', 3600],
    [604800, 'day', 86400], [2629800, 'week', 604800],
    [31557600, 'month', 2629800], [Infinity, 'year', 31557600],
  ];
  const [, unit, div] = units.find(([limit]) => abs < limit);
  const n = Math.round(secs / div);
  const plural = Math.abs(n) === 1 ? unit : `${unit}s`;
  return n < 0 ? `${Math.abs(n)} ${plural} ago` : `in ${n} ${plural}`;
};

// ─────────────────────────────────────────────
// Security event presentation
// ─────────────────────────────────────────────

const EVENTS = {
  login_success:           ['Signed in',               'green',  '→'],
  login_failed:            ['Failed sign-in attempt',  'amber',  '!'],
  auto_locked:             ['Locked automatically',    'red',    '⦸'],
  locked:                  ['Account locked',          'red',    '⦸'],
  unlocked:                ['Account unlocked',        'green',  '✓'],
  password_reset_by_admin: ['Password reset by admin', 'purple', '⟳'],
  password_changed:        ['Password changed',        'blue',   '✎'],
  activated:               ['Account activated',       'green',  '✓'],
  deactivated:             ['Account deactivated',     'gray',   '−'],
  mfa_required:            ['Two-step sign-in required', 'purple', '⚿'],
  mfa_not_required:        ['Two-step sign-in no longer required', 'gray', '⚿'],
  mfa_enrolled:            ['Authenticator app set up', 'green',  '⚿'],
  mfa_reset:               ['Authenticator reset by admin', 'purple', '⟳'],
  mfa_backup_code_used:    ['Signed in with a backup code', 'amber', '⚿'],
};

const eventMeta = (key) => EVENTS[key] || [String(key).replace(/_/g, ' '), 'gray', '•'];

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────

export default function UserProfilePage({ currentUser }) {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const userPhoto = useUserPhoto(data?.user?.user_id); // the viewed user's HR photo
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [impersonating, setImpersonating] = useState(false);
  const [confirmImpersonate, setConfirmImpersonate] = useState(false);
  const [dialog, setDialog] = useState(null);   // lock | unlock | reset | deactivate | reactivate
  const [reason, setReason] = useState('');
  const [tempPw, setTempPw] = useState('');
  const [hours, setHours] = useState('24');
  const [forceChange, setForceChange] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [outcome, setOutcome] = useState(null);

  // Synchronous, unlike the `busy` state — two clicks in the same tick would
  // both get past a state check before React re-renders and disables the
  // button, firing the same one-shot action twice.
  const runningRef = useRef(false);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      setData(await api.getUserProfile(userId));
      setError(null);
    } catch (e) {
      setError(e.message || 'Could not load this user.');
    }
    if (!silent) setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const closeDialog = () => {
    setDialog(null); setReason(''); setTempPw('');
    setHours('24'); setForceChange(true); setDialogError(null);
  };

  const run = async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setBusy(true); setDialogError(null);
    try {
      let res;
      if (dialog === 'lock') res = await api.lockUser(userId, reason.trim());
      else if (dialog === 'unlock') res = await api.unlockUser(userId);
      else if (dialog === 'mfa_reset') res = await api.resetUserMfa(userId);
      else if (dialog === 'reset') res = await api.adminResetPassword(userId, {
        temp_password: tempPw,
        expires_in_hours: Number(hours) || 24,
        force_change: forceChange,
      });
      else if (dialog === 'deactivate') res = await api.deleteUser(userId);
      else if (dialog === 'reactivate') res = await api.updateUser(userId, { is_active: true });

      const titles = {
        lock: 'Account Locked', unlock: 'Account Unlocked', mfa_reset: 'Two-Step Sign-in Reset',
        reset: forceChange ? 'Temporary Password Set' : 'Password Set',
        deactivate: 'User Deactivated', reactivate: 'User Reactivated',
      };
      // The temporary password is echoed back only on the reset outcome, and
      // only here — it is never emailed and only ever stored as a hash, so
      // this is the single chance to pass it on.
      setOutcome({
        title: titles[dialog] || 'Done',
        message: res?.message || 'The change has been saved.',
        secret: dialog === 'reset' ? tempPw : null,
        tone: (dialog === 'lock' || dialog === 'deactivate') ? 'red' : 'green',
      });
      closeDialog();
      await load({ silent: true });
    } catch (e) {
      setDialogError(e.message || 'That did not work.');
    }
    runningRef.current = false;
    setBusy(false);
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><RNLLoader title="Loading profile…" /></div>;
  }

  if (error || !data) {
    return (
      <div className="fade-in" style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 15, color: 'var(--red)', marginBottom: 16 }}>{error || 'User not found.'}</div>
        <Btn variant="secondary" onClick={() => navigate('/admin?tab=users')}>← Back to Users</Btn>
      </div>
    );
  }

  // Park the administrator's own session, take the impersonation token, then
  // reload from the root. The full reload is deliberate: every screen reads
  // the signed-in user once, so swapping identity in place would leave stale
  // data on screen belonging to the wrong account.
  const beginImpersonation = async () => {
    if (impersonating) return;
    setConfirmImpersonate(false);
    setImpersonating(true);
    try {
      if (!api.stashAdminSession()) {
        throw new Error('Could not save your own session. Sign in again and retry.');
      }
      const res = await api.impersonateUser(userId);
      const tokenValue = res?.access_token || res?.data?.access_token;
      if (!tokenValue) throw new Error('The server did not return a session for that user.');
      localStorage.setItem('pf_token', tokenValue);
      localStorage.removeItem('pf_refresh');   // impersonation sessions do not refresh
      api.setToken(tokenValue);
      api.clearApiCache();
      window.location.href = '/';
    } catch (e) {
      // Put the admin's session back — a failed attempt must not leave them
      // holding a parked token and a half-swapped state.
      api.restoreAdminSession();
      setImpersonating(false);
      // Reuses this page's own outcome dialog rather than window.alert, which
      // renders as a raw browser box outside the app's styling.
      setOutcome({
        title: 'Could not sign in as this user',
        tone: 'red',
        message: errorText(e, 'Could not sign in as this user.'),
      });
    }
  };

  const u = data.user || {};
  const s = data.security || {};
  const isSelf = currentUser?.user_id === u.user_id;
  // What the signed-in administrator may do here (Permission Matrix → Admin · Users), and whether this
  // account is theirs to change at all (a super-admin account is only changed by a super admin).
  const ua = userAdminAccess(currentUser);
  const manage = canManageUser(currentUser, u);
  // Only a super admin may sign in as somebody else, and never as themselves.
  const canImpersonate = !isSelf
    && !!currentUser?.roles?.some((r) => (r.role_name || r.name) === 'super_admin')
    && s.is_active && !s.is_locked;
  const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();

  // Locked and deactivated are shown separately — collapsing them into one
  // "inactive" badge would hide which of the two an admin has to undo.
  const statusBadges = [
    s.is_locked && ['Locked', 'red'],
    !s.is_active && ['Deactivated', 'gray'],
    s.is_active && !s.is_locked && ['Active', 'green'],
    s.is_verified ? ['Verified', 'green'] : ['Unverified', 'amber'],
    s.must_change_password && [
      s.temp_password_expired ? 'Temp Password Expired' : 'Temp Password',
      s.temp_password_expired ? 'red' : 'amber',
    ],
  ].filter(Boolean);

  // No complexity rules on an admin-set password, by client decision — only
  // an empty box is refused, and the server agrees.
  const canConfirm =
    dialog === 'lock' ? reason.trim().length >= 3
      : dialog === 'reset' ? tempPw.length > 0
        : true;

  const dialogTitles = {
    lock: 'Lock Account', unlock: 'Unlock Account', reset: 'Reset Password', mfa_reset: 'Reset Two-Step Sign-in',
    deactivate: 'Deactivate User', reactivate: 'Reactivate User',
  };
  const confirmLabels = {
    lock: 'Lock Account', unlock: 'Unlock', reset: 'Set Temporary Password', mfa_reset: 'Reset',
    deactivate: 'Deactivate', reactivate: 'Reactivate',
  };

  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <ProfileGridStyles />

      <BackBar label="Back to Users" onBack={() => navigate('/admin?tab=users')}>
        Currently viewing: <strong style={{ color: 'var(--text-primary)' }}>
          👤 {fullName || 'User'}
        </strong>
      </BackBar>

      {/* A locked or deactivated account gets a banner, because it is the one
          fact on this page that changes what the admin should do next. */}
      {(s.is_locked || !s.is_active) && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '13px 16px', borderRadius: 10,
          background: s.is_locked ? 'var(--red-dim)' : 'var(--gray-dim)',
          border: `1px solid ${s.is_locked ? 'rgba(239,68,68,0.35)' : 'var(--border-light)'}`,
        }}>
          <span style={{ fontSize: 17, lineHeight: 1.2 }}>{s.is_locked ? '⦸' : '−'}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: s.is_locked ? 'var(--red)' : 'var(--text-secondary)' }}>
              {s.is_locked
                ? (s.auto_locked ? 'Locked automatically after repeated failed sign-ins' : 'This account is locked')
                : 'This account is deactivated'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.55 }}>
              {s.is_locked ? (
                <>
                  {s.lock_reason && <em style={{ opacity: 0.9 }}>{s.lock_reason}</em>}
                  {s.locked_at && <> · {fmtDateTime(s.locked_at)}</>}
                  {s.locked_by_name && <> · by {s.locked_by_name}</>}
                </>
              ) : 'They cannot sign in. Their approval history is preserved.'}
            </div>
          </div>
        </div>
      )}

      <div className="user-profile-grid" style={PROFILE_GRID}>

        {/* ── Left rail ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0, height: '100%' }}>
          <Card style={{ padding: '26px 20px', textAlign: 'center', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
              <Avatar first={u.first_name} last={u.last_name} src={userPhoto || u.profile_pic} size={104} />
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.3 }}>
              {fullName || '—'}
            </div>
            {u.job_title && (
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 3 }}>{u.job_title}</div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, fontFamily: 'var(--mono)', wordBreak: 'break-all' }}>
              {u.email}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', marginTop: 14 }}>
              {statusBadges.map(([label, color]) => (
                <Badge key={label} color={color} pulse={label === 'Locked'}>{label}</Badge>
              ))}
            </div>

            <div style={{ borderTop: '1px solid var(--border)', marginTop: 18, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 9 }}>
              {[
                ['Created', data.created_at],
                ['Last updated', data.updated_at],
                ['Last sign-in', s.last_login_at],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                  <span style={{ color: 'var(--text-secondary)', fontWeight: 600, textAlign: 'right' }}>
                    {value ? (relative(value) || fmtDateTime(value)) : '—'}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* ── Actions ──────────────────────────────────────────── */}
          {/* flex:1 takes the leftover height in the rail; the buttons stay
              at the top of it rather than spreading out. */}
          <Panel title="Administration" pad={14} style={{ flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
              {/* The variant is the same throughout — actionFace supplies the
                  whole appearance, and Btn merges `style` after the variant.
                  Each action gets its own hue so the stack reads at a glance:
                  blue edits, purple credentials, red/green access, amber for
                  the lifecycle pair. Unlock and Reactivate deliberately differ
                  (green vs cyan) because an account can be both locked and
                  deactivated, putting both buttons on screen together. */}
              {canImpersonate && (
                <Btn variant="secondary" style={actionFace('blue')}
                  loading={impersonating}
                  onClick={() => setConfirmImpersonate(true)}>
                  ⇥ {impersonating ? 'Signing in…' : 'Login as User'}
                </Btn>
              )}
              {manage && (ua.edit || ua.assign_roles || ua.deactivate || ua.mfa) && (
              <Btn variant="secondary" style={actionFace('accent')}
                onClick={() => navigate(`/admin/users/${u.user_id}/edit`)}>
                ✎ Edit Details
              </Btn>
              )}

              {manage && ua.reset_password && (
              <Btn variant="secondary" style={actionFace('purple')}
                onClick={() => setDialog('reset')}>
                ⟳ Reset Password
              </Btn>
              )}

              {s.mfa_enrolled && manage && ua.mfa && (
                <Btn variant="secondary" style={actionFace('purple')}
                  onClick={() => setDialog('mfa_reset')}
                  title="Lost or replaced phone: they set up the authenticator app again at their next sign-in">
                  ⚿ Reset Two-Step Sign-in
                </Btn>
              )}

              {!(manage && ua.lock) ? null : s.is_locked ? (
                <Btn variant="secondary" style={actionFace('green')}
                  onClick={() => setDialog('unlock')}>
                  ✓ Unlock Account
                </Btn>
              ) : (
                <Btn variant="secondary" style={actionFace('red')}
                  disabled={isSelf} title={isSelf ? 'You cannot lock your own account.' : undefined}
                  onClick={() => setDialog('lock')}>
                  ⦸ Lock Account
                </Btn>
              )}

              {!(manage && ua.deactivate) ? null : s.is_active ? (
                <Btn variant="secondary" style={actionFace('amber')}
                  disabled={isSelf} title={isSelf ? 'You cannot deactivate your own account.' : undefined}
                  onClick={() => setDialog('deactivate')}>
                  − Deactivate User
                </Btn>
              ) : (
                <Btn variant="secondary" style={actionFace('cyan')}
                  onClick={() => setDialog('reactivate')}>
                  ✓ Reactivate User
                </Btn>
              )}

              {isSelf && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 2, lineHeight: 1.5, flexShrink: 0 }}>
                  This is your own account — locking and deactivating are disabled.
                </div>
              )}
            </div>
          </Panel>
        </div>

        {/* ── Right column ──────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>

          <Panel title="Personal Information">
            <FieldGrid>
              <Field label="First Name" value={u.first_name} />
              <Field label="Last Name" value={u.last_name} />
              <Field label="Email Address" value={u.email} mono />
              <Field label="Phone Number" value={u.phone} />
            </FieldGrid>
          </Panel>

          <Panel title="Staff Record">
            <FieldGrid>
              <Field label="Employee ID" value={u.employee_id} mono />
              <Field label="Job Title" value={u.job_title} />
              <Field label="Grade" value={u.grade} />
              <Field label="Branch" value={u.branch} />
            </FieldGrid>
          </Panel>

          <Panel title={`Roles (${u.roles?.length || 0})`}>
            {u.roles?.length ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 10 }}>
                {u.roles.map(r => (
                  <div key={r.role_id} style={{
                    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                    padding: '11px 14px', borderRadius: 8, minWidth: 0,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{r.display_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--mono)', marginTop: 2 }}>{r.role_name}</div>
                    {r.description && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 5, lineHeight: 1.5 }}>{r.description}</div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No roles assigned.</div>
            )}
          </Panel>

          <Panel title={`Departments (${u.departments?.length || 0})`}>
            {u.departments?.length ? (
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {u.departments.map(d => <Badge key={d.id} color="purple">{d.name}</Badge>)}
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No departments assigned.</div>
            )}
            {data.department_head_of?.length > 0 && (
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 7 }}>
                  Head of department
                </div>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  {data.department_head_of.map(n => <Badge key={n} color="indigo">{n}</Badge>)}
                </div>
              </div>
            )}
          </Panel>

          <Panel title="Security &amp; Access">
            <FieldGrid min={190}>
              <Field label="Account Status" value={
                <Badge color={s.is_locked ? 'red' : s.is_active ? 'green' : 'gray'}>
                  {s.is_locked ? 'Locked' : s.is_active ? 'Active' : 'Deactivated'}
                </Badge>
              } />
              <Field label="Email Verified" value={
                <Badge color={s.is_verified ? 'green' : 'amber'}>{s.is_verified ? 'Verified' : 'Pending'}</Badge>
              } />
              <Field label="Last Sign-in" value={s.last_login_at ? fmtDateTime(s.last_login_at) : 'Never signed in'} />
              <Field
                label={`Failed Attempts (locks at ${s.max_failed_login_attempts ?? 5})`}
                value={
                  <span style={{ color: s.failed_login_attempts > 0 ? 'var(--amber)' : undefined, fontWeight: 700 }}>
                    {s.failed_login_attempts ?? 0}
                    {s.last_failed_login_at ? ` · last ${relative(s.last_failed_login_at)}` : ''}
                  </span>
                }
              />

              <Field label="Password State" value={
                s.must_change_password ? (
                  <Badge color={s.temp_password_expired ? 'red' : 'amber'}>
                    {s.temp_password_expired ? 'Temporary — expired' : 'Temporary — change required'}
                  </Badge>
                ) : <Badge color="green">Set by the user</Badge>
              } />
              <Field
                label="Temp Password Expires"
                value={s.temp_password_expires_at
                  ? `${fmtDateTime(s.temp_password_expires_at)}${relative(s.temp_password_expires_at) ? ` (${relative(s.temp_password_expires_at)})` : ''}`
                  : null}
              />
              <Field label="Password Last Changed" value={s.password_changed_at ? fmtDateTime(s.password_changed_at) : 'Never changed'} />
              <Field label="Last Reset By" value={s.password_reset_by_name} />
              <Field label="Two-Step Sign-in" value={
                !s.mfa_required ? 'Not required'
                  : s.mfa_enrolled ? `On since ${fmtDateTime(s.mfa_enrolled_at)} · ${s.mfa_backup_codes_left} backup code${s.mfa_backup_codes_left === 1 ? '' : 's'} left`
                    : 'Required — set up at next sign-in'} />

              {s.is_locked && (
                <>
                  <Field label="Locked At" value={fmtDateTime(s.locked_at)} />
                  <Field label="Locked By" value={s.auto_locked ? 'System (automatic)' : s.locked_by_name} />
                  <Field label="Lock Reason" value={s.lock_reason} full />
                </>
              )}
            </FieldGrid>
          </Panel>

          <Panel
            title="Recent Activity"
            right={<span style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)' }}>{data.recent_events?.length || 0} most recent</span>}
            pad={0}
          >
            {data.recent_events?.length ? (
              <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                {data.recent_events.map((ev, i) => {
                  const [label, color, glyph] = eventMeta(ev.event);
                  return (
                    <div key={ev.id} style={{
                      display: 'flex', gap: 12, padding: '12px 18px', alignItems: 'flex-start',
                      borderBottom: i === data.recent_events.length - 1 ? 'none' : '1px solid var(--border)',
                    }}>
                      <span style={{
                        width: 26, height: 26, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 800,
                        background: `color-mix(in srgb, var(--${color}) 14%, transparent)`,
                        color: `var(--${color})`,
                        border: `1px solid color-mix(in srgb, var(--${color}) 35%, transparent)`,
                      }}>{glyph}</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{label}</div>
                        {ev.detail && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2, lineHeight: 1.5 }}>{ev.detail}</div>
                        )}
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                          {fmtDateTime(ev.created_at)}
                          {relative(ev.created_at) ? ` · ${relative(ev.created_at)}` : ''}
                          {ev.actor_name ? ` · by ${ev.actor_name}` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ padding: 18, fontSize: 13, color: 'var(--text-muted)' }}>
                Nothing recorded yet.
              </div>
            )}
          </Panel>
        </div>
      </div>

      {/* ── Confirmation dialogs ────────────────────────────────── */}
      {dialog && (
        <Modal
          title={dialogTitles[dialog]}
          onClose={busy ? () => { } : closeDialog}
          width={dialog === 'reset' ? 560 : 460}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 0' }}>

            {dialog === 'lock' && (
              <>
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{fullName}</strong> will be signed out
                  and blocked from signing in again until an administrator unlocks the account.
                  Their records and approval history are untouched.
                </p>
                <Textarea label="Reason (required)" rows={3} value={reason} onChange={setReason}
                  placeholder="Why is this account being locked?" />
              </>
            )}

            {dialog === 'mfa_reset' && (
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--text-primary)' }}>{fullName}</strong>'s authenticator app and backup codes stop working.
                {s.mfa_required ? ' Two-step sign-in stays required, so they will scan a new QR code at their next sign-in.' : ''}
                {' '}Use this when they have lost or replaced their phone.
              </p>
            )}

            {dialog === 'unlock' && (
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--text-primary)' }}>{fullName}</strong> will be able to sign in
                again, and the failed-attempt counter resets to zero.
                {s.auto_locked && ' This lock was applied automatically after repeated failed sign-ins.'}
              </p>
            )}

            {dialog === 'reset' && (
              <>
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  Choose a password for <strong style={{ color: 'var(--text-primary)' }}>{fullName}</strong> and
                  pass it to them yourself.
                </p>

                <Input label="New password" type="password" value={tempPw} onChange={setTempPw}
                  placeholder="Type the password to give them" />

                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                  background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                  padding: '12px 14px', borderRadius: 8,
                }}>
                  <input
                    type="checkbox"
                    style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                    checked={forceChange}
                    onChange={e => setForceChange(e.target.checked)}
                  />
                  <span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                      Force a password change at next sign-in
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, lineHeight: 1.5 }}>
                      {forceChange
                        ? 'This is a temporary password — they must replace it before they can use the system.'
                        : 'They can sign in with this password and keep using it.'}
                    </span>
                  </span>
                </label>

                {/* An expiry only means anything alongside a forced change; a
                    password they are allowed to keep must not have one. */}
                {forceChange && (
                  <Select label="Valid for" value={hours} onChange={setHours} options={[
                    { value: '4', label: '4 hours' },
                    { value: '8', label: '8 hours' },
                    { value: '24', label: '24 hours (default)' },
                    { value: '48', label: '48 hours' },
                    { value: '72', label: '3 days' },
                    { value: '168', label: '7 days' },
                  ]} />
                )}

                {s.is_locked && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    This account is currently locked — resetting the password will also unlock it.
                  </div>
                )}
              </>
            )}

            {dialog === 'deactivate' && (
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--text-primary)' }}>{fullName}</strong> will no longer be able
                to sign in and will disappear from approver lists. Nothing is deleted, and you can
                reactivate them at any time.
              </p>
            )}

            {dialog === 'reactivate' && (
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--text-primary)' }}>{fullName}</strong> will be able to sign in
                again and will reappear in approver lists.
              </p>
            )}

            {dialogError && (
              <div style={{
                fontSize: 12.5, color: 'var(--red)', background: 'var(--red-dim)',
                border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8,
                padding: '9px 12px', lineHeight: 1.55,
              }}>
                {dialogError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
              <Btn variant="ghost" disabled={busy} onClick={closeDialog}>Cancel</Btn>
              <Btn
                variant={(dialog === 'lock' || dialog === 'deactivate') ? 'danger' : 'primary'}
                loading={busy}
                disabled={!canConfirm}
                onClick={run}
              >
                {confirmLabels[dialog]}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Confirm impersonation ───────────────────────────────── */}
      {confirmImpersonate && (
        <Modal title="Sign in as this user" onClose={() => setConfirmImpersonate(false)} width={480}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '6px 0' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 800,
                background: 'color-mix(in srgb, var(--blue) 14%, transparent)',
                color: 'var(--blue)',
                border: '1px solid color-mix(in srgb, var(--blue) 35%, transparent)',
              }}>⇥</span>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                You will be signed in as <strong style={{ color: 'var(--text-primary)' }}>{fullName}</strong> with
                their full access.
              </p>
            </div>

            {/* Said plainly rather than buried: the audit trail records the
                window, but the work itself is filed under this user's name. */}
            <div style={{
              background: 'var(--amber-dim)', border: '1px solid color-mix(in srgb, var(--amber) 35%, transparent)',
              borderRadius: 8, padding: '10px 12px', fontSize: 12.5,
              color: 'var(--text-secondary)', lineHeight: 1.55,
            }}>
              Anything you do will be recorded as {u.first_name}, not as you. The start and
              end of this session are written to their security trail.
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 }}>
              <Btn variant="ghost" disabled={impersonating} onClick={() => setConfirmImpersonate(false)}>Cancel</Btn>
              <Btn variant="primary" loading={impersonating} onClick={beginImpersonation}>
                {impersonating ? 'Signing in…' : `Sign in as ${u.first_name}`}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Outcome ─────────────────────────────────────────────── */}
      {outcome && (
        <Modal title={outcome.title} onClose={() => setOutcome(null)} width={480}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '6px 0' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 800,
                background: `color-mix(in srgb, var(--${outcome.tone}) 14%, transparent)`,
                color: `var(--${outcome.tone})`,
                border: `1px solid color-mix(in srgb, var(--${outcome.tone}) 35%, transparent)`,
              }}>{outcome.tone === 'red' ? '⦸' : '✓'}</span>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {outcome.message}
              </p>
            </div>

            {outcome.secret && (
              <div style={{
                background: 'var(--bg-elevated)', border: '1px dashed var(--border-light)',
                borderRadius: 8, padding: '12px 14px',
              }}>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                  Password — pass this to the user
                </div>
                <div style={{
                  fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 700,
                  color: 'var(--text-primary)', letterSpacing: '0.04em', wordBreak: 'break-all',
                }}>
                  {outcome.secret}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 7, lineHeight: 1.5 }}>
                  It is stored only as a hash, so this will not be shown again.
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Btn onClick={() => setOutcome(null)}>Done</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
